import React from 'react';
import styles from './index.css';
import { getPhtvperiodlist } from '../../../../../services/api';
import errorBoundary from '@ifeng-fe/errorBoundary';

const PHTV_CN = 'phtvChinese';
const PHTV_HK = 'phtvHK';
const PHTV_NEWS = 'phtvNews';
const DEFAULT_CHANNEL = PHTV_NEWS;
const PHTV_LINKS = {
    [PHTV_CN]: './live?channel=phtvChinese',
    [PHTV_HK]: './live?channel=phtvHK',
    [PHTV_NEWS]: './live?channel=phtvNews',
};
const WEEK_TEXT = ['星期一', '星期二', '星期三', '星期四', '星期五', '星期六', '星期日'];

/**
 * 用于给单位的日/月补0，如 9 -> 09
 * @param {number} value - 日/月
 * @returns {string}
 */
const addZero = value => {
    return value < 10 ? `0${value}` : String(value);
};

/**
 * 将任意时区的时间戳转化为北京时间的Date实例
 * @param {number} now - 本地时间戳，不传则默认使用当前本地时间
 * @returns {object} 北京时间的Date实例
 */
const getBeijingTime = now => {
    const timestamp = now || Date.now();

    return new Date(timestamp + 60 * 1000 * (new Date().getTimezoneOffset() + 480));
};

/**
 * 将传入的Date实例转化为 yyyy-MM-dd 的格式
 * @param {object} date - Date实例
 * @returns {string} yyyy-MM-dd格式的字符串
 */
const formatDate = date => {
    return `${date.getFullYear()}-${addZero(date.getMonth() + 1)}-${addZero(date.getDate())}`;
};

/**
 * 获取本周日期数组（周一到周日），如今天是2021-4-15，周四，那么返回值为：["2021-04-12", "2021-04-13", "2021-04-14", "2021-04-15", "2021-04-16", "2021-04-17", "2021-04-18"]
 * @returns {array}
 */
const getDates = () => {
    const newDate = getBeijingTime();
    const timestamp = newDate.getTime();
    const currenDay = newDate.getDay();
    const dates = [];

    for (let i = 0; i < 7; i++) {
        const date = getBeijingTime(timestamp + 24 * 60 * 60 * 1000 * (i - ((currenDay + 6) % 7)));

        dates.push(formatDate(date));
    }

    return dates;
};

/**
 * 增强日期数组
 * @param {array} dates - 如 ["2021-04-12", "2021-04-13", "2021-04-14", "2021-04-15", "2021-04-16", "2021-04-17", "2021-04-18"]
 * @returns {array} 如 [{"date": "2021-04-12", "text": "星期一"}, ... {"date": "2021-04-18", "text": "星期日"}]
 */
const enhanceDates = dates => {
    return WEEK_TEXT.map((item, index) => ({ date: dates[index], text: item }));
};

/**
 * 兼容IE的new Date()，原因是IE不支持 new Date("2021/4/16 09:24") 这种写法
 * @param {string} dateStr - 如 "2021/4/16 09:24" 这种形式的字符串
 * @returns {object} date对象
 */
const newDateIE = dateStr => {
    const [date, time] = dateStr.split(' ');
    const newDate = new Date(date);
    const [hour, min, sec] = time.split(':').map(item => parseInt(item));

    newDate.setHours(hour, min, sec);

    return newDate;
};

/**
 * 用于获取查询字符串中的频道，从林哥那里抄来的，之后梳理的时候合并一下
 * @returns {string} 从查询字符串中获取的当前频道
 */
const getChannel = () => {
    const channels = ['phtvHK', 'phtvNews', 'phtvChinese'];

    if (window) {
        for (const channel of channels) {
            if (window.location.search.includes(channel)) {
                return channel;
            }
        }

        return 'phtvNews';
    } else {
        return 'phtvNews';
    }
};

/**
 * 输入日期，在该日期的开始时间，在该日期的结束时间，返回对应的开始时间戳和结束时间戳数组
 * @param {string} date - 形如 2021-04-20 的字符串
 * @param {string} start - 形如 13:34:00 的字符串
 * @param {string} end - 形如 13:34:00 的字符串
 * @returns {array} 起始时间戳和结束时间戳组成的数组
 */
const getTimestampInRange = (date, start, end) => {
    const formatDate = date.replace(/-/g, '/');
    const startTimeStamp = getBeijingTime(newDateIE(`${formatDate} ${start}`).getTime()).getTime();
    const endTimeStamp = getBeijingTime(newDateIE(`${formatDate} ${end}`).getTime()).getTime();

    return [startTimeStamp, endTimeStamp];
};

class MainBox extends React.PureComponent {
    dates = getDates();
    state = {
        activeChannel: DEFAULT_CHANNEL,
        activeDate: formatDate(getBeijingTime()),
        periodList: {},
        timestampNow: getBeijingTime().getTime(),
    };

    async componentDidMount() {
        const { dates } = this;
        const periodList = await getPhtvperiodlist(dates[0], [dates[dates.length - 1]]);

        this.setState({
            periodList,
        });

        this.timer = setInterval(() => {
            this.setState({
                timestampNow: getBeijingTime().getTime(),
            });
        }, 1000 * 10);

        this.setState({
            activeChannel: getChannel(),
        });
    }

    componentWillUnmount() {
        clearInterval(this.timer);
    }

    handleLeftBarClick = key => {
        this.setState({
            activeChannel: key,
        });
    };

    handleWeekBarClick = key => {
        this.setState({
            activeDate: key,
        });
    };

    getPeriodList() {
        const { activeChannel, activeDate, periodList, timestampNow } = this.state;

        if (periodList && periodList[activeDate]) {
            const dateList = periodList[activeDate];

            if (dateList && typeof dateList === 'object') {
                const tvList = dateList[activeChannel];

                if (tvList && Array.isArray(tvList) && tvList.length) {
                    return tvList.map((item, index, array) => {
                        const { time, title } = item;
                        const date = activeDate;
                        const start = `${time}:00`;
                        const unformatEnd = array[index + 1] && array[index + 1].time;
                        const end = unformatEnd ? `${unformatEnd}:00` : '23:59:59';
                        const [startTimeStamp, endTimeStamp] = getTimestampInRange(date, start, end);
                        const ifInRange = timestampNow >= startTimeStamp && timestampNow < endTimeStamp;

                        // if (ifInRange) {
                        //     return (
                        //         <a
                        //             key={`${activeDate}-${activeChannel}-${time}-${title}`}
                        //             className={`${styles.listItem} ${styles.active}`}
                        //             href={PHTV_LINKS[activeChannel]}
                        //             target="_blank">
                        //             <div className={styles.listItemTime}>{time}</div>
                        //             <div className={styles.listItemTitle}>{title}</div>
                        //             <div className={styles.listItemLiveIcon} />
                        //         </a>
                        //     );
                        // } else {
                        //     return (
                        //         <div
                        //             key={`${activeDate}-${activeChannel}-${time}-${title}`}
                        //             className={styles.listItem}>
                        //             <div className={styles.listItemTime}>{time}</div>
                        //             <div className={styles.listItemTitle}>{title}</div>
                        //         </div>
                        //     );
                        // }

                        return (
                            <div key={`${activeDate}-${activeChannel}-${time}-${title}`} className={styles.listItem}>
                                <div className={styles.listItemTime}>{time}</div>
                                <div className={styles.listItemTitle}>{title}</div>
                            </div>
                        );
                    });
                } else {
                    return <div className={styles.listNoItem}>暂无数据</div>;
                }
            }
        }

        return null;
    }

    render() {
        const { activeChannel, activeDate } = this.state;
        const { dates } = this;

        return (
            <div className={styles.main}>
                <div className={styles.leftbar}>
                    <div className={styles.leftBarTitle}>节目单</div>
                    <div
                        className={`${styles.leftbarItem} ${activeChannel === PHTV_NEWS ? styles.active : ''}`}
                        onClick={() => {
                            this.handleLeftBarClick(PHTV_NEWS);
                        }}>
                        资讯台
                    </div>
                    <div
                        className={`${styles.leftbarItem} ${activeChannel === PHTV_CN ? styles.active : ''}`}
                        onClick={() => {
                            this.handleLeftBarClick(PHTV_CN);
                        }}>
                        中文台
                    </div>
                    {/* <div
                        className={`${styles.leftbarItem} ${activeChannel === PHTV_HK ? styles.active : ''}`}
                        onClick={() => {
                            this.handleLeftBarClick(PHTV_HK);
                        }}>
                        香港台
                    </div> */}
                </div>
                <div className={styles.content}>
                    <div className={styles.weekBar}>
                        {enhanceDates(dates).map(item => {
                            const { date, text } = item;

                            return (
                                <div
                                    key={date}
                                    className={`${styles.weekItem} ${activeDate === date ? styles.active : ''}`}
                                    onClick={() => {
                                        this.handleWeekBarClick(date);
                                    }}>
                                    <div className={styles.weekItemText}>{text}</div>
                                    <div className={styles.weekItemDate}>{date}</div>
                                </div>
                            );
                        })}
                    </div>
                    <div className={styles.list} key={`list_${activeChannel}_${activeDate}`}>
                        <div className={styles.listHead}>
                            <div className={styles.listHeadTime}>播出时间</div>
                            <div className={styles.listHeadTitle}>节目名称</div>
                        </div>
                        {this.getPeriodList()}
                    </div>
                </div>
            </div>
        );
    }
}

export default errorBoundary(MainBox);
